import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import markDark from "@/assets/lx-mark-dark.png";

const links = [
  { label: "Home", target: "top" },
  { label: "Project in de kijker", target: "project-in-de-kijker" },
  { label: "Realisaties", target: "realisaties" },
  { label: "Over ons", target: "over-ons" },
];

const mobileMenuItemAnimation = (index) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.06 * index + 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
});

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const scrollTo = (target) => {
    if (target === "top") {
      window.scrollTo({ top: 0 });
      return;
    }
    const el = document.getElementById(target);
    if (!el) return;
    el.scrollIntoView();
  };

  const go = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
    } else {
      setTimeout(() => scrollTo(target), 60);
    }
  };

  const solid = scrolled || open;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        solid
          ? "bg-white/85 backdrop-blur-xl border-b border-ink/5 py-3"
          : "bg-transparent border-b border-transparent py-6"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="flex items-center py-1"
          aria-label="LX Bouw — home"
        >
          <img src={markDark} alt="LX Bouw logo" className="h-9 md:h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-${l.target}`}
              onClick={() => go(l.target)}
              className={`font-body text-sm tracking-wide link-underline transition-colors duration-500 active:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 ${
                solid
                  ? "text-slate2 hover:text-ink focus-visible:ring-forest/60 focus-visible:ring-offset-white"
                  : "text-white hover:text-white focus-visible:ring-white/90 focus-visible:ring-offset-transparent"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-contact-cta"
            onClick={() => go("contact")}
            className="group inline-flex items-center gap-2 rounded-cta bg-forest text-white px-6 py-3 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-forestdark hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2"
          >
            Contact
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-ink relative z-50 rounded-full p-2 -mr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest/50"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Sluit menu" : "Open menu"}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="lg:hidden fixed inset-0 z-40 bg-offwhite flex flex-col"
            >
              <nav className="flex-1 px-6 pt-[116px] pb-12 flex flex-col overflow-y-auto">
                {links.map((l, i) => (
                  <motion.button
                    key={l.target}
                    data-testid={`nav-mobile-${l.target}`}
                    onClick={() => go(l.target)}
                    {...mobileMenuItemAnimation(i)}
                    className="text-left font-heading font-light text-4xl text-ink py-5 border-b border-ink/5 focus-visible:outline-none focus-visible:text-forest"
                  >
                    {l.label}
                  </motion.button>
                ))}

                <motion.div
                  {...mobileMenuItemAnimation(links.length)}
                  className="mt-10"
                >
                  <button
                    data-testid="nav-mobile-contact-cta"
                    onClick={() => go("contact")}
                    className="group w-full inline-flex items-center justify-center gap-3 rounded-cta bg-forest text-white px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:bg-forestdark hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2"
                  >
                    Contact
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};

export default Navbar;
